// Store for the auto-generated CV accompanying texts (Phase 1).
//
// Two key spaces with different lifetimes (ARCHITECTURE §5.2):
//   * artifact content  -> keyed `${offerId}:${mode}`   (survives a mode switch,
//     which is what makes the C4 reuse feel instant)
//   * panel session state -> keyed `offer_id`           (UX §11 R-2 hard requirement:
//     CVModalForm remounts its subtree on every mode change, so component-local
//     state would be destroyed exactly when the recruiter needs it)
//
// THE STORE NEVER WRITES `manual_text` (FE-11). It holds the suggestion and the
// session bookkeeping; only the four allow-listed methods in the CV modals may
// touch the textarea.

export const POLL_INTERVAL_MS = 5000
export const POLL_MAX_TICKS = 40 // 40 x 5s = 200s hard stop (ARCHITECTURE §5.2)
export const PENDING_LONG_MS = 60000
export const CONFIRM_MIN_CHARS = 20

export const MODE_RANK = { normal: 0, lite: 1, super_lite: 2 }

export const MODE_LABELS = {
  normal: 'רגיל',
  lite: 'לייט',
  super_lite: 'סופר לייט'
}

export const artifactKey = (offerId, mode) => `${offerId}:${mode}`

// `cvOtherMixin.findOrCreateOffer` builds a VIRTUAL offer whose id is
// `temp_<Date.now()>` — the position exists, the offer does not yet. Every CV
// text endpoint is keyed on a real `offers.id`, so generating or polling for one
// of these ids is a guaranteed 404/422 loop: the poller would hammer /status
// every 5 s for the life of the page and the panel would sit in `pending`
// forever. Only a positive integer is a real offer.
export const isRealOfferId = offerId => {
  if (typeof offerId === 'number') return Number.isFinite(offerId) && offerId > 0 && offerId % 1 === 0
  if (typeof offerId !== 'string') return false
  return /^[1-9][0-9]*$/.test(offerId.trim())
}

// Timers live outside the reactive state on purpose: a setInterval handle is not
// data, and putting it in `state` makes Vue walk it on every mutation.
const timers = {}

export const clearAllTimers = () => {
  Object.keys(timers).forEach(key => {
    clearInterval(timers[key])
    delete timers[key]
  })
}

export const activeTimerKeys = () => Object.keys(timers)

const emptyPanel = () => ({
  applied: null, // { appliedText, appliedMode, appliedKind: 'use' | 'append' }
  undoSnapshot: null, // { previousText, previousFlags }
  undoDone: false,
  expanded: false,
  confirmOpen: false,
  confirmKind: null, // 'use' | 'append' | 'ai-button'
  cautionSourceMode: null,
  pendingShown: false,
  lastProgrammaticValue: null,
  telemetry: { suggestion_applied: false, suggestion_source_mode: null, suggestion_text: '' }
})

const emptyMeta = () => ({
  requestedAt: null,
  ticks: 0,
  gaveUp: false,
  gaveUpRounds: 0,
  modeSwitchPending: false,
  regenerating: false,
  error: null
})

const normalizeFlag = value => {
  if (typeof value === 'boolean') return value
  if (value === null || value === undefined) return false
  const raw = String(value).trim().toLowerCase()
  return raw === 'true' || raw === '1' || raw === 'yes'
}

// R-4. The server puts `reused_from_mode` INSIDE each artifacts[] entry (it is
// per-kind: professional notes can be an exact-mode hit while the personal
// impression is reused from a more restrictive mode). There has never been a
// top-level `envelope.reused_from_mode`, so the old read produced `undefined`
// forever: the reuse label never rendered, and `appliedMode` fell back to the
// DISPLAY mode — which is what is stored as `suggestion_source_mode` on the
// send record, so both the shadow report's C4 join and the M3 dashboard were
// being told the wrong source.
//
// The value returned is the source mode of the READY artifacts, and only when
// EVERY contributing artifact was reused. A mixed envelope (one exact, one
// reused) has no single source; its least restrictive contributor is the
// display mode itself, and reporting that as "reused" would understate the
// anonymity level of the combined text.
export const reusedSourceMode = envelope => {
  const ready = ((envelope && envelope.artifacts) || []).filter(a => a && a.status === 'ready')
  if (ready.length === 0) return null
  if (ready.some(a => !a.reused_from_mode)) return null
  return ready
    .map(a => a.reused_from_mode)
    .reduce((acc, mode) => {
      if (acc === null) return mode
      const a = MODE_RANK[acc]
      const b = MODE_RANK[mode]
      if (a === undefined) return mode
      if (b === undefined) return acc
      return b < a ? mode : acc
    }, null)
}

// F-2. The safe-direction mode switch (rank(new) < rank(old), e.g. לייט -> רגיל)
// already HAS a displayable text on the client: the more-restrictive artifact is
// exactly what the server will hand back as a C4 reuse a round-trip later. Until
// that round-trip lands there is no envelope for the new key, so the panel
// collapsed to `off`/`pending` and then re-rendered `ready` — a visible flicker
// on a switch that changes nothing about the text.
//
// Returns the CLOSEST more-restrictive ready envelope for the same offer, or
// null. Being restricted to `rank > rank(mode)` is what confines this to the
// safe direction: the unsafe one (רגיל -> לייט) finds nothing here and keeps its
// `mode-switch-pending` copy, because a `normal` text genuinely must not be
// shown under `lite` (C5).
export const fallbackReadyEnvelope = (artifacts, offerId, mode) => {
  const target = MODE_RANK[mode]
  if (target === undefined) return null
  const prefix = `${offerId}:`
  let best = null
  Object.keys(artifacts || {}).forEach(key => {
    if (key.indexOf(prefix) !== 0) return
    const candidateMode = key.slice(prefix.length)
    const rank = MODE_RANK[candidateMode]
    if (rank === undefined || rank <= target) return
    const candidate = artifacts[key]
    if (!candidate || candidate.enabled === false) return
    if (!(candidate.suggested_text || '').length) return
    if (best === null || rank < best.rank) best = { rank: rank, mode: candidateMode, envelope: candidate }
  })
  return best
}

// Ordered derivation of the visible panel state (UX §7.2). Exported so the
// component and its specs read exactly the same rules.
export const derivePanelState = ({ envelope, panel, meta, now, fallback }) => {
  const at = now === undefined ? Date.now() : now
  const session = panel || emptyPanel()
  const info = meta || emptyMeta()
  const view = {
    state: 'off',
    partialPending: false,
    suggestedText: '',
    parts: [],
    reusedFromMode: null,
    reuseLabel: null,
    fallbackFromMode: null,
    debounceRemainingMs: 0,
    canRegenerate: false,
    // Is there suggestion text behind a COLLAPSED applied/undo row, i.e. would
    // expanding it actually show something? False whenever the row was reached
    // through the no-ready branch (the cross-mode unsafe-switch window, or a
    // purged key), where `הצג הצעה` would expand into nothing.
    canReopen: false
  }

  // An explicitly disabled envelope is the feature being OFF and always wins;
  // a MISSING one is just the request not having landed yet, which is the
  // window the F-2 fallback exists to cover.
  if (envelope && envelope.enabled === false) return view

  const primaryReady = !!envelope && (envelope.suggested_text || '').length > 0
  const primaryTerminal = !!envelope && envelope.terminal === true
  const usableFallback = (!primaryReady && !primaryTerminal && fallback) ? fallback : null
  if (!envelope && !usableFallback) return view

  const source = usableFallback ? usableFallback.envelope : envelope
  const artifacts = source.artifacts || []
  const suggested = source.suggested_text || ''
  const hasReady = suggested.length > 0
  view.suggestedText = suggested
  view.parts = source.parts || []
  view.reusedFromMode = usableFallback ? usableFallback.mode : reusedSourceMode(source)
  // Rendered verbatim. The server owns this string (Cv::TextArtifactService::
  // REUSE_LABEL) so the panel and the shadow report cannot drift apart. A
  // fallback render has no server label for THIS mode, so the panel composes the
  // local one from `reusedFromMode` instead.
  view.reuseLabel = usableFallback ? null : (source.reuse_label || null)
  view.fallbackFromMode = usableFallback ? usableFallback.mode : null
  view.debounceRemainingMs = info.debouncedUntil ? Math.max(0, info.debouncedUntil - at) : 0

  const isTerminalStatus = s => s === 'ready' || s === 'failed' || s === 'skipped'
  const pendingKinds = artifacts.filter(a => !isTerminalStatus(a.status))
  const skippedKinds = artifacts.filter(a => a.status === 'skipped')
  const allTerminal = source.terminal === true ||
    (artifacts.length > 0 && pendingKinds.length === 0)

  view.partialPending = hasReady && pendingKinds.length > 0
  const regenerable = () =>
    !usableFallback && artifacts.some(a => a.status === 'failed') && view.debounceRemainingMs === 0

  // BLOCKER-1. `applied` / `undo-done` used to return BEFORE `session.expanded`
  // was ever read, so both were permanently collapsed: `הצג הצעה` and the
  // applied cue dispatched setExpanded(true), the store recorded it, and the
  // derivation threw it away on the next tick. That is a dead end — UX §7.2
  // promises the suggestion "is never discarded and can be re-opened", and it
  // was the ONLY way back to the text after applying.
  //
  // Expanded now renders the suggestion card WITH the applied/undo status row
  // beneath it (the row keeps `בטלי`, the card keeps `כווץ`), so the round trip
  // collapsed -> expanded -> collapsed closes in both directions.
  if (session.undoDone) {
    view.state = session.expanded && hasReady ? 'undo-done-expanded' : 'undo-done'
    view.canRegenerate = view.state === 'undo-done-expanded' && regenerable()
    view.canReopen = hasReady
    return view
  }
  if (session.applied) {
    view.state = session.expanded && hasReady ? 'applied-expanded' : 'applied-collapsed'
    view.canRegenerate = view.state === 'applied-expanded' && regenerable()
    view.canReopen = hasReady
    return view
  }
  if (hasReady) {
    if (session.confirmOpen) {
      view.state = 'confirm-replace'
    } else if (view.partialPending) {
      view.state = info.regenerating ? 'regenerating-partial' : 'ready-expanded'
      // One half is ready and the other is still coming. The plain
      // `ready-collapsed` copy claimed the whole suggestion was done, so she
      // applied half a text believing it was all of it.
      if (!session.expanded) view.state = 'ready-collapsed-partial'
    } else {
      view.state = session.expanded ? 'ready-expanded' : 'ready-collapsed'
    }
    view.canRegenerate = regenerable()
    return view
  }

  if (info.gaveUp) {
    view.state = 'gave-up'
    // §12.3: `צרי מחדש` re-enters polling, but only for one more bounded window.
    view.canRegenerate = info.gaveUpRounds < 2
    return view
  }
  if (artifacts.length > 0 && skippedKinds.length === artifacts.length) {
    view.state = session.pendingShown ? 'skipped-visible' : 'skipped-silent'
    return view // no regenerate link — it would only fail
  }
  if (allTerminal) {
    view.state = view.debounceRemainingMs > 0 ? 'debounced' : 'failed'
    view.canRegenerate = view.debounceRemainingMs === 0
    return view
  }
  if (view.debounceRemainingMs > 0) {
    view.state = 'debounced'
    return view
  }
  if (info.regenerating) {
    view.state = 'regenerating'
    return view
  }
  if (info.modeSwitchPending) {
    view.state = 'mode-switch-pending'
    return view
  }
  if (info.requestedAt && at - info.requestedAt > PENDING_LONG_MS) {
    view.state = 'pending-long'
    return view
  }
  view.state = 'pending'
  return view
}

export const cueForPanelState = panelState => {
  switch (panelState) {
    case 'pending':
    case 'pending-long':
    case 'mode-switch-pending':
    case 'regenerating':
    case 'regenerating-partial':
      return 'preparing'
    case 'ready-collapsed':
    case 'ready-collapsed-partial':
    case 'ready-expanded':
    case 'confirm-replace':
      return 'ready'
    case 'applied-collapsed':
    case 'applied-expanded':
    case 'undo-done':
    case 'undo-done-expanded':
      return 'applied'
    case 'failed':
    case 'gave-up':
    case 'skipped-visible':
    case 'debounced':
      return 'unavailable'
    default:
      return 'absent'
  }
}

// Factory, not a literal: a shared object would be reused by every store built
// from this module, which leaks state across tests and across hot reloads.
const state = () => ({
  flags: {},
  flagsLoaded: false,
  artifacts: {}, // `${offerId}:${mode}` -> envelope
  meta: {}, // `${offerId}:${mode}` -> poller / lifecycle bookkeeping
  panels: {} // offerId -> session state
})

const mutations = {
  setFlags (state, flags) {
    const normalized = {}
    Object.keys(flags || {}).forEach(key => {
      normalized[key] = normalizeFlag(flags[key])
    })
    state.flags = normalized
    state.flagsLoaded = true
  },
  setArtifact (state, { offerId, mode, envelope }) {
    state.artifacts = { ...state.artifacts, [artifactKey(offerId, mode)]: envelope }
  },
  clearArtifact (state, { offerId, mode }) {
    const next = { ...state.artifacts }
    delete next[artifactKey(offerId, mode)]
    state.artifacts = next
  },
  setMeta (state, { offerId, mode, patch }) {
    const key = artifactKey(offerId, mode)
    state.meta = {
      ...state.meta,
      [key]: { ...emptyMeta(), ...(state.meta[key] || {}), ...patch }
    }
  },
  tick (state, { offerId, mode }) {
    const key = artifactKey(offerId, mode)
    const current = state.meta[key] || emptyMeta()
    state.meta = { ...state.meta, [key]: { ...current, ticks: current.ticks + 1 } }
  },
  initPanel (state, offerId) {
    if (state.panels[offerId]) return
    state.panels = { ...state.panels, [offerId]: emptyPanel() }
  },
  setPanel (state, { offerId, patch }) {
    const current = state.panels[offerId] || emptyPanel()
    state.panels = { ...state.panels, [offerId]: { ...current, ...patch } }
  },
  clearPanel (state, offerId) {
    const next = { ...state.panels }
    delete next[offerId]
    state.panels = next
  }
}

const actions = {
  async fetchFlags ({ commit, state }, { vm, force }) {
    if (state.flagsLoaded && !force) return state.flags
    try {
      const data = await vm.$network_utils.getCvTextFlags()
      commit('setFlags', data || {})
    } catch (err) {
      // A dead flags endpoint must never break the CV modal: everything stays off.
      commit('setFlags', {})
    }
    return state.flags
  },

  // Kicks generation off and starts the poller. Never writes manual_text.
  async generate ({ commit, dispatch, getters }, { offerId, mode, vm, modeSwitch }) {
    if (!getters.aiTextsEnabled) return null
    // A virtual `temp_<ts>` offer has no row to generate against (see
    // isRealOfferId): the POST is a guaranteed error and the poller it starts
    // never terminates.
    if (!isRealOfferId(offerId)) return null
    commit('initPanel', offerId)
    commit('setMeta', {
      offerId,
      mode,
      patch: {
        requestedAt: Date.now(),
        ticks: 0,
        gaveUp: false,
        regenerating: false,
        modeSwitchPending: modeSwitch === true,
        error: null
      }
    })
    try {
      const envelope = await vm.$network_utils.generateCvText({ offer_id: offerId, mode: mode })
      commit('setArtifact', { offerId, mode, envelope: envelope || { enabled: false } })
    } catch (err) {
      commit('setMeta', { offerId, mode, patch: { error: 'request_failed' } })
    }
    dispatch('startPolling', { offerId, mode, vm })
    return null
  },

  async fetchStatus ({ commit, state }, { offerId, mode, vm }) {
    try {
      const envelope = await vm.$network_utils.getCvTextStatus({ offer_id: offerId, mode: mode })
      commit('setArtifact', { offerId, mode, envelope: envelope || { enabled: false } })
      return envelope
    } catch (err) {
      commit('setMeta', { offerId, mode, patch: { error: 'request_failed' } })
      return null
    }
  },

  // One timer per key: calling startPolling twice with the same key must not
  // create a second interval (FE-10).
  startPolling ({ commit, dispatch, state }, { offerId, mode, vm }) {
    // Same guard as `generate`: never start a 5 s interval against an offer id
    // the API cannot resolve.
    if (!isRealOfferId(offerId)) return
    const key = artifactKey(offerId, mode)
    if (timers[key]) return
    timers[key] = setInterval(() => {
      const meta = state.meta[key] || emptyMeta()
      if (meta.ticks >= POLL_MAX_TICKS) {
        // Hard stop. The artifact may still finish server-side, so this is a
        // distinguishable `gave-up` state, not `failed` (UX R-10).
        dispatch('stopPolling', { offerId, mode })
        commit('setMeta', {
          offerId,
          mode,
          patch: { gaveUp: true, gaveUpRounds: (meta.gaveUpRounds || 0) + 1, regenerating: false }
        })
        return
      }
      commit('tick', { offerId, mode })
      dispatch('fetchStatus', { offerId, mode, vm }).then(envelope => {
        if (envelope && envelope.terminal === true) {
          dispatch('stopPolling', { offerId, mode })
          commit('setMeta', { offerId, mode, patch: { regenerating: false, modeSwitchPending: false } })
        }
      })
    }, POLL_INTERVAL_MS)
  },

  stopPolling (_ctx, { offerId, mode }) {
    const key = artifactKey(offerId, mode)
    if (!timers[key]) return
    clearInterval(timers[key])
    delete timers[key]
  },

  stopAllPolling () {
    clearAllTimers()
  },

  // Targets ONE failed kind (UX R-9). From `gave-up` it instead re-enters polling
  // for the kinds that are still pending (§12.3), bounded to one more window.
  async regenerate ({ commit, dispatch, state }, { offerId, mode, kind, vm }) {
    const key = artifactKey(offerId, mode)
    const meta = state.meta[key] || emptyMeta()

    if (meta.gaveUp) {
      if (meta.gaveUpRounds >= 2) return null
      commit('setMeta', { offerId, mode, patch: { gaveUp: false, ticks: 0, requestedAt: Date.now() } })
      dispatch('startPolling', { offerId, mode, vm })
      return null
    }

    commit('setMeta', { offerId, mode, patch: { regenerating: true, error: null } })
    try {
      const envelope = await vm.$network_utils.regenerateCvText({ offer_id: offerId, mode: mode, kind: kind })
      commit('setArtifact', { offerId, mode, envelope: envelope || { enabled: false } })
      commit('setMeta', { offerId, mode, patch: { ticks: 0, requestedAt: Date.now() } })
      dispatch('startPolling', { offerId, mode, vm })
      return envelope
    } catch (err) {
      const body = (err && err.response && err.response.data) || {}
      if (body.error === 'debounced') {
        commit('setMeta', {
          offerId,
          mode,
          patch: {
            regenerating: false,
            debouncedUntil: Date.now() + (body.retry_after_ms || 0)
          }
        })
        return body
      }
      commit('setMeta', { offerId, mode, patch: { regenerating: false, error: 'request_failed' } })
      return null
    }
  },

  // ---- panel session state -------------------------------------------------

  markPendingShown ({ commit }, offerId) {
    commit('initPanel', offerId)
    commit('setPanel', { offerId, patch: { pendingShown: true } })
  },

  setExpanded ({ commit }, { offerId, value }) {
    commit('initPanel', offerId)
    commit('setPanel', { offerId, patch: { expanded: value } })
  },

  setConfirmOpen ({ commit }, { offerId, value, kind }) {
    commit('initPanel', offerId)
    commit('setPanel', { offerId, patch: { confirmOpen: value, confirmKind: value ? kind : null } })
  },

  // Records an apply. The CALLER writes manual_text — this only books the state.
  recordApplied ({ commit }, { offerId, appliedText, appliedMode, appliedKind, previousText, previousFlags }) {
    commit('initPanel', offerId)
    commit('setPanel', {
      offerId,
      patch: {
        applied: { appliedText, appliedMode, appliedKind },
        undoSnapshot: { previousText, previousFlags },
        undoDone: false,
        confirmOpen: false,
        confirmKind: null,
        cautionSourceMode: appliedMode,
        telemetry: {
          suggestion_applied: true,
          suggestion_source_mode: appliedMode,
          suggestion_text: appliedText
        }
      }
    })
  },

  // Undo restores the pre-apply telemetry verbatim and clears the caution band (§12.5).
  recordUndo ({ commit, state }, offerId) {
    const panel = state.panels[offerId] || emptyPanel()
    const previousFlags = (panel.undoSnapshot && panel.undoSnapshot.previousFlags) || {
      suggestion_applied: false,
      suggestion_source_mode: null,
      suggestion_text: ''
    }
    commit('setPanel', {
      offerId,
      patch: {
        applied: null,
        undoSnapshot: null,
        undoDone: true,
        cautionSourceMode: null,
        telemetry: { ...previousFlags }
      }
    })
  },

  // The recruiter took ownership: hide undo, drop the snapshot, clear telemetry.
  clearSuggestionTelemetry ({ commit }, offerId) {
    commit('initPanel', offerId)
    commit('setPanel', {
      offerId,
      patch: {
        applied: null,
        undoSnapshot: null,
        undoDone: false,
        cautionSourceMode: null,
        telemetry: { suggestion_applied: false, suggestion_source_mode: null, suggestion_text: '' }
      }
    })
  },

  setLastProgrammaticValue ({ commit }, { offerId, value }) {
    commit('initPanel', offerId)
    commit('setPanel', { offerId, patch: { lastProgrammaticValue: value } })
  },

  // R-9: walk the TIMERS, not the artifacts. A poller started by `generate`
  // whose request then failed has a timer but no artifact entry, so the old
  // artifact-driven walk left it running after the modal closed — a 5 s poll
  // against a dead component, forever, for every failed generate of the
  // session. Timers are the thing being stopped; they are the thing to iterate.
  closeSession ({ commit, dispatch }, offerId) {
    const prefix = `${offerId}:`
    activeTimerKeys().forEach(key => {
      if (key.indexOf(prefix) !== 0) return
      dispatch('stopPolling', { offerId, mode: key.slice(prefix.length) })
    })
    commit('clearPanel', offerId)
  }
}

const getters = {
  uiEnabled: state => state.flags.CV_PATTERN_UI_ENABLED === true,
  aiTextsEnabled: state => state.flags.CV_AI_TEXTS_ENABLED === true,
  envelopeFor: state => (offerId, mode) => state.artifacts[artifactKey(offerId, mode)] || null,
  metaFor: state => (offerId, mode) => state.meta[artifactKey(offerId, mode)] || emptyMeta(),
  panelFor: state => offerId => state.panels[offerId] || emptyPanel(),
  panelView: state => (offerId, mode, now) => derivePanelState({
    envelope: state.artifacts[artifactKey(offerId, mode)],
    panel: state.panels[offerId],
    meta: state.meta[artifactKey(offerId, mode)],
    fallback: fallbackReadyEnvelope(state.artifacts, offerId, mode),
    now
  }),
  cueState: (state, getters) => (offerId, mode, now) => cueForPanelState(getters.panelView(offerId, mode, now).state),
  telemetryFor: (state, getters) => offerId => getters.panelFor(offerId).telemetry,
  // Caution band: the applied text was generated for a LESS restrictive mode
  // than the one currently selected (E-9 anonymity leak).
  cautionModeFor: (state, getters) => (offerId, currentMode) => {
    const source = getters.panelFor(offerId).cautionSourceMode
    if (!source || !(source in MODE_RANK) || !(currentMode in MODE_RANK)) return null
    return MODE_RANK[source] < MODE_RANK[currentMode] ? source : null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
